/* eslint-disable @typescript-eslint/no-explicit-any */
import {ChangeDetectorRef, DestroyRef, Injectable} from '@angular/core';
import {NgEventBus} from 'ng-event-bus';
import {SpatialUtilService} from '../../../../services/spatial-util.service';
import {MapToolsService} from '../../../../services/map-tools.service';
import {ConstructionToolModelService} from './construction-tool-model.service';
import {EsriModuleService} from '../../../../esri-nlr/services/esri-module.service';
import {SpatialSequenceService} from '../../../../../common/service/spatial-sequence.service';
import {ConstructionUtilService} from './construction-util.service';
import {LookupService} from '../../../../../common/service/lookup.service';
import {UserPreferencesService} from '../../../../../common/service/user-preferences.service';
import {AngleUtilService} from '../../../../services/features/angle/angle-util.service';
import {SpatialSnapshotService} from '../../../../snapshot/spatial-snapshot.service';
import {PolygonUtilService} from '../../../../services/features/polygon-util.service';
import {AnnotationService} from '../../../../services/features/annotation.service';
import {LineTypeLookup} from '../../../../../common/models/line-type.model';
import {PointTypeLookup} from '../../../../../common/models/point-type.model';
import {LegalParcelTypeLookup} from '../../../../../common/models/legal-parcel-type.model';
import {map, switchMap} from 'rxjs/operators';
import {BehaviorSubject, interval, of, Subscription, tap} from 'rxjs';
import {FeatureAttributesLookup} from '../../../../../common/models/feature-attributes.model';
import {equals} from '../../../../../common/service/data-util';
import {forkJoinDefaultIfEmpty} from '../../../../../common/observable.utils';
import {WindowWidgetsService} from '../../../../window-widgets/window-widgets.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ToolServiceInterface} from './tool-service-interface';

@Injectable({
    providedIn: 'root'
})
export class MergeLinesConstructionToolService implements ToolServiceInterface {
    title = 'MergeLinesConstructionToolService';

    attributes: any[];

    private bearingReversed = false;

    private clearSubscription: Subscription;

    private closeSubscription: Subscription;

    private attributesSubject: BehaviorSubject<object[]>;

    private connectedLinesSubject: BehaviorSubject<string>;

    private selectedLinesSubject: BehaviorSubject<string>;

    private distanceSubject: BehaviorSubject<string>;

    private updateSubjectValuesIntervalSubscription: Subscription;

    constructor(
        private annotationService: AnnotationService,
        private angleUtilService: AngleUtilService,
        private constructionToolModelService: ConstructionToolModelService,
        private constructionUtilService: ConstructionUtilService,
        private esriModuleService: EsriModuleService,
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private polygonUtilService: PolygonUtilService,
        private mapToolsService: MapToolsService,
        private spatialSequenceService: SpatialSequenceService,
        private spatialSnapshotService: SpatialSnapshotService,
        private spatialUtilService: SpatialUtilService,
        private userPreferencesService: UserPreferencesService,
        private windowWidgetsService: WindowWidgetsService,
        private destroyRef: DestroyRef
    ) {}

    setupFeatureEventModification(options: {changeDetectorRef: ChangeDetectorRef}) {
        const attributes = this.constructionToolModelService.getToolAttrs('mergeLines');

        if (!this.attributes) {
            this.initDistanceCalculationMethod(attributes);
        }

        console.info(`${this.title} - setupFeatureEventModification`, attributes);

        this.attributes = attributes;

        if (!this.clearSubscription) {
            this.clearSubscription = this.eventBus
                .on('constructionToolService:mergeLines:clear')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.clear());
        }

        if (!this.closeSubscription) {
            this.closeSubscription = this.eventBus
                .on('constructionTool:mergeLines:close')
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.clear();
                    if (this.clearSubscription) {
                        this.clearSubscription.unsubscribe();
                        this.clearSubscription = null;
                    }

                    if (this.closeSubscription) {
                        this.closeSubscription.unsubscribe();
                        this.closeSubscription = null;
                    }

                    this.destroySubjects();
                });
        }

        this.initSubjects();

        this.validate();

        console.log(`${this.title} - setupFeatureEventModification - this.attributes:`, this.attributes);

        if (this.attributes.find(attribute => attribute.name === 'Distance' && attribute.value === undefined)) {
            this.initDistanceValue();
        }
    }

    showAttribute(attrName: string) {
        const lines = this.getSelectedMergeableLines();

        if (
            this.getConnectedLines().length < 2 ||
            this.openTraverseCheck(this.pointsCount(lines), lines) ||
            this.businessClassCheck(lines).notPermitted.length
        ) {
            return false;
        }

        if (attrName === 'type') {
            return true;
        }

        const typeAttr = this.attributes && this.attributes.find(a => a.prop === 'type');

        return typeAttr && typeAttr.value;
    }

    // NOTE: This method is referenced from merge-lines-bearing.html
    reverseBearing(attribute: any) {
        if (attribute.value) {
            attribute.value = this.angleUtilService.subtract(attribute.value, 180);

            this.bearingReversed = !this.bearingReversed;
        }
    }

    create(viewModel: any) {
        console.debug(`${this.title} - create`);
        return this.mapToolsService.activate('CONSTRUCTION_TOOLS_MERGE_LINES', () => {
            const newLineAttrs = this.spatialUtilService
                .getLineMapLayer()
                .fields.map(field => field.name)
                .filter(k => Object.hasOwn(viewModel, k))
                .reduce((acc, k) => {
                    const viewModelItem = viewModel[k];
                    acc[k] = viewModelItem && typeof viewModelItem === 'object' ? viewModelItem.id : viewModelItem;
                    return acc;
                }, {});

            this.validate();

            if (this.getTool().errors.length === 0) {
                const subscription = this.spatialSnapshotService
                    .createSnapshot()
                    .pipe(switchMap(() => this.mergeLines(newLineAttrs)))
                    .subscribe();
                this.eventBus.cast('nlr:busy', subscription);
            }
        });
    }

    private getTool() {
        return this.constructionToolModelService.getTool('mergeLines');
    }

    private getLegalParcelTypeLookups(): LegalParcelTypeLookup[] {
        return this.lookupService.getLookups('legalParcelType');
    }

    private getLineTypeLookups(): LineTypeLookup[] {
        return this.lookupService.getLookups('lineType');
    }

    private getPointTypeLookups(): PointTypeLookup[] {
        return this.lookupService.getLookups('pointType');
    }

    private getControlPointTypeLookups(): PointTypeLookup[] {
        return this.lookupService.getLookups('controlPointType');
    }

    private getFeatureAttributesLookups(): FeatureAttributesLookup[] {
        return this.lookupService.getLookups('featureAttributes');
    }

    private mergeLines(newLineAttrs: any) {
        // get the lines to delete
        const linesToDelete: any[] = this.getConnectedLines();
        const linesToDeleteIds: number[] = linesToDelete.map(l => l.attributes.id);

        // get connected points
        const allConnectedPtIds: number[] = this.getAllConnectedPointIds(linesToDelete);
        const connectedPtsIds: number[] = this.getUniqueConnectedPtIds(allConnectedPtIds);
        const endPtIds: number[] = this.getStartAndEndPointIds(allConnectedPtIds);
        const middlePtIds: number[] = this.getMiddlePoints(allConnectedPtIds);

        const lines: any[] = this.spatialUtilService.getLines();

        const unselectedMiddlePointLines = lines.filter(
            l =>
                !linesToDeleteIds.includes(l.attributes.id) && middlePtIds.some(id => [l.attributes.frompointid, l.attributes.topointid].includes(id))
        );

        const unselectedMiddlePointSpurLinePoints: any[] = this.spatialUtilService
            .getPoints()
            .filter(
                (p: any) =>
                    !middlePtIds.includes(p.attributes.id) &&
                    unselectedMiddlePointLines.some(l => [l.attributes.frompointid, l.attributes.topointid].includes(p.attributes.id)) &&
                    lines.filter(l => [l.attributes.frompointid, l.attributes.topointid].includes(p.attributes.id)).length === 1
            );

        const unselectedMiddlePointLineAngles: any[] = this.spatialUtilService
            .getAngles()
            .filter((a: any) =>
                unselectedMiddlePointLines.some(l => [a.attributes.anglefromlineid, a.attributes.angletolineid].includes(l.attributes.id))
            );

        const edits: {createdLine?: any; polygon?: any; polyline?: any} = {};

        // update the associated lines
        return this.updateAssociatedLines(linesToDelete, endPtIds, newLineAttrs).pipe(
            tap((createdLine: any) => (edits.createdLine = createdLine)),

            // update total lot boundaries
            switchMap(() => this.updateTotalLotBoundaries(linesToDelete, endPtIds, connectedPtsIds, edits.createdLine)),

            // update the associated polygons
            switchMap(() => this.updateAssociatedPolygons(linesToDelete, edits.createdLine, unselectedMiddlePointLines)),
            tap(polygonEdits => (edits.polygon = polygonEdits)),

            // update the associated angles
            switchMap(() => this.updateAssociatedAngles(middlePtIds, endPtIds, linesToDeleteIds, edits.createdLine)),

            //update polyLines
            switchMap(() => this.updateAssociatedPolyLines(endPtIds, middlePtIds, connectedPtsIds)),
            tap(polylineEdits => (edits.polyline = polylineEdits)),

            // update lots
            switchMap(() => this.updateAssociatedParentFeatures(this.spatialUtilService.getLotMapLayer(), {polygon: edits.polygon})),

            // update roads
            switchMap(() => this.updateAssociatedParentFeatures(this.spatialUtilService.getRoadMapLayer(), {polygon: edits.polygon})),

            // update interests
            switchMap(() => this.updateAssociatedInterestFeatures({polygon: edits.polygon, polyline: edits.polyline})),

            // delete polygons
            switchMap(() => this.constructionUtilService.deleteFeatures(this.spatialUtilService.getPolygonMapLayer(), edits.polygon.deletes)),

            // delete points
            switchMap(() =>
                this.constructionUtilService.deleteFeatures(
                    this.spatialUtilService.getPointMapLayer(),
                    this.spatialUtilService.getPoints().filter(p => middlePtIds.includes(p.attributes.id))
                )
            ),

            // delete control points
            switchMap(() =>
                this.constructionUtilService.deleteFeatures(
                    this.spatialUtilService.getControlPointMapLayer(),
                    this.spatialUtilService.getControlPoints().filter(p => middlePtIds.includes(p.attributes.id))
                )
            ),

            // delete all the selected lines
            switchMap(() => this.constructionUtilService.deleteFeatures(this.spatialUtilService.getLineMapLayer(), linesToDelete)),

            // delete unselected middle point lines
            switchMap(() => this.constructionUtilService.deleteFeatures(this.spatialUtilService.getLineMapLayer(), unselectedMiddlePointLines)),

            // delete unselected middle point line angles
            switchMap(() => this.constructionUtilService.deleteFeatures(this.spatialUtilService.getAngleMapLayer(), unselectedMiddlePointLineAngles)),

            // delete spur line points
            switchMap(() =>
                this.constructionUtilService.deleteFeatures(this.spatialUtilService.getPointMapLayer(), unselectedMiddlePointSpurLinePoints)
            ),

            // delete offset Features
            switchMap(() => this.deleteAssociatedOffsetFeatures(middlePtIds, linesToDeleteIds)),

            // delete invalid road annotations
            switchMap(() => this.spatialUtilService.deleteInvalidRoadAnnotations()),

            // delete invalid interest annotations
            switchMap(() => this.spatialUtilService.deleteInvalidInterestAnnotations()),

            tap(() => {
                this.spatialUtilService.unhighlightAllFeatures();

                this.eventBus.cast('clearSelectedFeatures');
            })
        );
    }

    private initBearingAttribute() {
        const lineBearingFeatureAttrsLookup = this.getFeatureAttributesLookups().find(l => l.featureType === 'line' && l.prop === 'bearing');
        const lineTypesWithBearing = lineBearingFeatureAttrsLookup.validSubTypeIds.split(',').map(v => parseInt(v));

        let attrBeforeIdx = this.attributes.findIndex(a => a.prop === 'distanceCalculationMethod');
        attrBeforeIdx = attrBeforeIdx === -1 ? this.attributes.findIndex(a => a.prop === 'type') : attrBeforeIdx;

        if (attrBeforeIdx !== -1) {
            const bearingIdx = this.attributes.findIndex(a => a.prop === 'bearing');
            const typeAttr = this.attributes.find(a => a.prop === 'type');

            if (typeAttr.value && lineTypesWithBearing.includes(typeAttr.value.id)) {
                if (bearingIdx !== attrBeforeIdx + 1) {
                    const bearing =
                        bearingIdx !== -1
                            ? this.attributes[bearingIdx]
                            : {
                                  template: 'components/design/widgets/construction-tools/construction-tool/templates/merge-lines-bearing.html',
                                  featureType: 'mergeLines',
                                  name: 'Bearing',
                                  prop: 'bearing',
                                  display: true,
                                  editable: true,
                                  validSubTypeIds: lineBearingFeatureAttrsLookup.validSubTypeIds,
                                  editableOrderToolsWidget: this.attributes[attrBeforeIdx].editableOrderToolsWidget + 0.5
                              };

                    if (bearingIdx !== -1) {
                        this.attributes.splice(bearingIdx, 1);
                    }

                    this.attributes.splice(attrBeforeIdx + 1, 0, bearing);

                    this.initBearingValue();
                }
            } else if (bearingIdx !== -1) {
                this.attributes.splice(bearingIdx, 1);
            }
        }
    }

    private initBearingValue() {
        this.bearingReversed = false;
        const connectedLines = this.getConnectedLines();
        const bearingAttr = this.attributes.find(a => a.prop === 'bearing');
        if (bearingAttr && connectedLines.length >= 2) {
            if (connectedLines.every((v, i, a) => v.attributes.bearing && v.attributes.bearing === a[0].attributes.bearing)) {
                bearingAttr.value = connectedLines[0].attributes.bearing;
            } else {
                const startAndEndPointIds = this.getStartAndEndPointIds(this.getAllConnectedPointIds(connectedLines));

                const p0 = this.getPointsByType([startAndEndPointIds[0]])[0];

                const p1 = this.getPointsByType([startAndEndPointIds[1]])[0];

                bearingAttr.value = this.angleUtilService.calcBearingDms(p0.geometry.x, p0.geometry.y, p1.geometry.x, p1.geometry.y);
            }
        }
    }

    private initDistanceCalculationMethod(attrs: any[]) {
        const distanceCalculationMethodAttr = attrs.find(a => a.prop === 'distanceCalculationMethod');
        if (distanceCalculationMethodAttr) {
            distanceCalculationMethodAttr.value =
                this.userPreferencesService.userPreferences?.mergeLinesDistanceCalculationMethod === 'SUM_SELECTED_LINES' ? 'YES' : 'NO';
        }
    }

    private initDistanceValue() {
        const connectedLines = this.getConnectedLines();
        const distanceAttr = this.attributes.find(a => a.prop === 'distance');
        const distanceCalculationMethodAttr = this.attributes.find(a => a.prop === 'distanceCalculationMethod');
        if (distanceAttr && distanceCalculationMethodAttr && connectedLines.length >= 2) {
            if (distanceCalculationMethodAttr.value === 'YES') {
                // Sum selected lines
                distanceAttr.value = parseFloat(connectedLines.reduce((acc, v) => acc + v.attributes.distance, 0).toFixed(3));
            } else {
                // Calculate selected lines
                const startAndEndPointIds = this.getStartAndEndPointIds(this.getAllConnectedPointIds(connectedLines));

                const p0 = this.getPointsByType([startAndEndPointIds[0]])[0];

                const p1 = this.getPointsByType([startAndEndPointIds[1]])[0];

                distanceAttr.value = this.constructionUtilService.getDistanceBetweenPoints(p0, p1);
            }
            return distanceAttr.value;
        }
        return 0;
    }

    private getSelectedMergeableLines(): any[] {
        return this.spatialUtilService
            .getHighlightedLines()
            .filter((v: any) => v.attributes.frompointid && v.attributes.topointid && !v.attributes.lineids);
    }

    private getSelectedMergeableLineIdsString() {
        return this.getSelectedMergeableLines()
            .map(v => v.attributes.id)
            .join();
    }

    private destroySubjects() {
        if (this.updateSubjectValuesIntervalSubscription) {
            this.updateSubjectValuesIntervalSubscription.unsubscribe();
            this.updateSubjectValuesIntervalSubscription = null;
        }

        if (this.attributesSubject) {
            this.attributesSubject.complete();
            this.attributesSubject = null;
        }

        if (this.connectedLinesSubject) {
            this.connectedLinesSubject.complete();
            this.connectedLinesSubject = null;
        }

        if (this.selectedLinesSubject) {
            this.selectedLinesSubject.complete();
            this.selectedLinesSubject = null;
        }

        if (this.distanceSubject) {
            this.distanceSubject.complete();
            this.distanceSubject = null;
        }
    }

    private initSubjects() {
        // Destroy subjects if they exist
        this.destroySubjects();

        // Create subjects
        this.attributesSubject = new BehaviorSubject<object[]>([]);

        this.connectedLinesSubject = new BehaviorSubject<string>('');

        this.selectedLinesSubject = new BehaviorSubject<any>('');

        this.distanceSubject = new BehaviorSubject<any>('');

        // Update subject values
        this.updateSubjectValues();

        // Subscribe subjects
        this.attributesSubject.subscribe(() => {
            this.initBearingAttribute();
            this.validate();
        });

        this.connectedLinesSubject.subscribe(() => () => this.initBearingValue());

        this.selectedLinesSubject.subscribe(() => this.validateAndPopulateAttributes());

        this.distanceSubject.subscribe(() => this.initDistanceValue());

        // Update subject values on interval
        this.updateSubjectValuesIntervalSubscription = interval(250).subscribe(() => this.updateSubjectValues());
    }

    private updateSubjectValues() {
        if (!equals(this.attributesSubject.getValue(), this.attributes)) {
            this.attributesSubject.next(this.windowWidgetsService.structuredClone(this.attributes));
        }

        const connectedLinesBearings = this.getConnectedLines()
            .map(v => `${v.attributes.id}:${v.attributes.bearing}`)
            .join();
        if (this.connectedLinesSubject.getValue() !== connectedLinesBearings) {
            this.connectedLinesSubject.next(connectedLinesBearings);
        }

        const selectedLinesSummary = this.getSelectedMergeableLineIdsString();
        if (selectedLinesSummary !== this.selectedLinesSubject.getValue()) {
            this.selectedLinesSubject.next(selectedLinesSummary);
        }

        const typeAttr = this.attributes.find(a => a.prop === 'type');
        const type = typeAttr?.value?.id || '';
        const distanceCalculationMethodAttr = this.attributes.find(a => a.prop === 'distanceCalculationMethod');
        const distanceCalculationMethod = distanceCalculationMethodAttr?.value || 'NA';
        const connectedLinesDistance = this.getConnectedLines()
            .map(v => `${v.attributes.id}:${v.attributes.distance}`)
            .join();
        const distanceSummary = `type:${type}|distanceCalculationMethod:${distanceCalculationMethod}|connectedLinesSummary:${connectedLinesDistance}`;
        if (distanceSummary !== this.distanceSubject.getValue()) {
            this.distanceSubject.next(distanceSummary);
        }

        this.windowWidgetsService.refreshAll();
    }

    private validateAndPopulateAttributes() {
        this.validate();

        const selectedLines = this.getSelectedMergeableLines();
        let editable = this.businessClassCheck(selectedLines).notPermitted.length === 0;
        const group = [];

        const lineTypeLookups = this.getLineTypeLookups();

        const selectedLineTypes = selectedLines.reduce((acc, v) => {
            const lineType = v.attributes.type;

            if (group.length === 0) {
                group.push(...lineTypeLookups.find(f => f.id === lineType).validLineTypesOnUpdate);
            }

            if (!acc.includes(lineType)) {
                acc.push(lineType);
            }

            if (!group.includes(lineType)) {
                editable = false;
            }

            return acc;
        }, []);

        this.attributes.forEach(attribute => (attribute.editable = editable));

        const typeAttr = this.attributes.find(attribute => attribute.prop === 'type');
        if (typeAttr) {
            if (editable) {
                const selectedLineTypeLookups = lineTypeLookups.filter(l => selectedLineTypes.includes(l.id));

                const validLineTypes = selectedLineTypeLookups
                    .reduce(
                        (acc, v) => {
                            acc.push(...v.validLineTypesOnUpdate);
                            return acc;
                        },
                        selectedLineTypeLookups.map(l => l.id)
                    )
                    .sort()
                    .filter((v, i, a) => a.indexOf(v) === i);

                typeAttr.values = lineTypeLookups.filter(l => validLineTypes.includes(l.id));
                typeAttr.value = typeAttr.values.find((f: any) => f.id === selectedLineTypes[0]);
            } else {
                typeAttr.values = [];
                typeAttr.value = undefined;
            }
        }

        if (selectedLines.length) {
            console.debug(`${this.title} - validateAndPopulateAttributes - this.attributes: `, this.attributes);
            this.attributes
                .filter(attr => !['type', 'distance', 'distanceCalculationMethod'].includes(attr.prop))
                .forEach((attr: {attributeType?: string; prop?: string; values?: any[]; value?: any}) => {
                    if (
                        selectedLines.every((f: any) => f.attributes[attr.prop] && f.attributes[attr.prop] === selectedLines[0].attributes[attr.prop])
                    ) {
                        if (attr.values && attr.values.length > 0) {
                            if (attr.attributeType === 'DOMAIN' || attr.attributeType === 'RADIO') {
                                attr.values.forEach((l: any) => {
                                    if (attr.attributeType === 'DOMAIN') {
                                        if (l.id === parseInt(selectedLines[0].attributes[attr.prop])) {
                                            attr.value = l;
                                        }
                                    }
                                    if (attr.attributeType === 'RADIO') {
                                        if (String(l.value) === String(selectedLines[0].attributes[attr.prop])) {
                                            attr.value = l.value;
                                        }
                                    }
                                });
                            }
                        } else {
                            attr.value = selectedLines[0].attributes[attr.prop];
                        }
                    } else {
                        attr.value = undefined;
                    }
                });
        }

        this.initDistanceCalculationMethod(this.attributes);

        this.initDistanceValue();
    }

    private clear() {
        this.validateAndPopulateAttributes();
    }

    private getEndPoints() {
        const linesToDelete = this.getConnectedLines();
        const allConnectedPointIds = this.getAllConnectedPointIds(linesToDelete);
        return this.getStartAndEndPointIds(allConnectedPointIds);
    }

    /**
     * Find all construct point/line features that contain association with the deleted point as parameters and then delete the found offset features.
     */
    private deleteAssociatedOffsetFeatures(middlePtIds: any[], lineIds: any[]) {
        const constructLines: any[] = this.spatialUtilService
            .getConstructLines()
            .filter(
                (f: any) =>
                    middlePtIds.includes(parseInt(`${f.attributes.frompointid}`.match(/\d+/)[0])) ||
                    (f.attributes.lineid && lineIds.includes(f.attributes.lineid))
            );

        if (constructLines.length === 0) {
            return of(null);
        }

        const constructLineIds = constructLines.map(f => f.attributes.id);

        const constructLineMapLayer = this.spatialUtilService.getConstructLineMapLayer();

        // delete construct lines
        return this.constructionUtilService.deleteFeatures(constructLineMapLayer, constructLines).pipe(
            map(() =>
                // find associated construct points
                this.spatialUtilService
                    .getConstructPoints()
                    .filter(
                        (f: any) =>
                            f.attributes.sourcelineids &&
                            constructLineIds.some(constructLineId => f.attributes.sourcelineids.includes(`[${constructLineId}]`))
                    )
            ),
            switchMap((constructPoints: any[]) => {
                if (constructPoints.length === 0) {
                    return of(null);
                }

                return this.constructionUtilService.deleteFeatures(this.spatialUtilService.getConstructPointMapLayer(), constructPoints).pipe(
                    map(() =>
                        this.spatialUtilService
                            .getConstructLines()
                            .filter(
                                (f: any) =>
                                    !constructLineIds.includes(f.attributes.id) &&
                                    constructPoints.some(
                                        point =>
                                            (point.attributes.sourcelineids && point.attributes.sourcelineids.includes(`[${f.attributes.id}]`)) ||
                                            point.attributes.id === f.attributes.frompointid ||
                                            point.attributes.id === f.attributes.topointid
                                    )
                            )
                    ),
                    switchMap((additionalConstructLines: any) => {
                        // delete any additional construct lines
                        if (additionalConstructLines.length === 0) {
                            return of(null);
                        }

                        return this.constructionUtilService.deleteFeatures(constructLineMapLayer, additionalConstructLines);
                    })
                );
            })
        );
    }

    private updateAssociatedInterestFeatures(childEdits: {
        polygon?: {updates?: any[]; deletes?: any[]};
        polyline?: {updates?: any[]; deletes?: any[]};
    }) {
        if (
            childEdits.polygon &&
            childEdits.polygon.updates.length === 0 &&
            childEdits.polygon.deletes.length === 0 &&
            childEdits.polyline &&
            childEdits.polyline.updates.length === 0 &&
            childEdits.polyline.deletes.length === 0
        ) {
            return of(null);
        }

        const featureLayer = this.spatialUtilService.getInterestMapLayer();

        const polygonUpdates: any[] = childEdits.polygon?.updates?.map(u => new this.esriModuleService.Graphic(u.toJson())) || [];
        const polygonDeletes = childEdits.polygon?.deletes || [];
        const polygonUpdatesIds = polygonUpdates.map(p => p.attributes.id);
        const polygonDeletesIds = polygonDeletes.map(p => p.attributes.id);
        const polygonEditsIds = [...polygonUpdatesIds, ...polygonDeletesIds];

        const polylineUpdates = childEdits.polyline?.updates?.map(u => new this.esriModuleService.Graphic(u.toJson())) || [];
        const polylineDeletes = childEdits.polyline?.deletes || [];
        const polylineUpdatesIds = polylineUpdates.map(p => p.attributes.id);
        const polylineDeletesIds = polylineDeletes.map(p => p.attributes.id);
        const polylineEditsIds = [...polylineUpdatesIds, ...polylineDeletesIds];

        // Get lots associated with polygons
        const lotsWithEditedPolygons: any[] = this.spatialUtilService
            .getLots()
            .filter((lot: any) => lot.attributes.polygonids && polygonEditsIds.some(id => lot.attributes.polygonids.includes(`[${id}]`)));

        const polygonLotInfo = {
            allBurdenedLotIds: lotsWithEditedPolygons
                .map(lot => lot.attributes.name)
                .filter(v => v)
                .map(v => parseInt(v)),
            allBurdenedLotPolygonIds: lotsWithEditedPolygons.reduce((acc, lot) => {
                acc.push(
                    ...this.spatialUtilService
                        .parseIdsAttr(lot.attributes.polygonids)
                        .filter((id: number) => !acc.includes(id) && !polygonDeletesIds.includes(id))
                );
                return acc;
            }, []),
            burdenedLotPolygonIdsMap: lotsWithEditedPolygons.reduce((acc, lot) => {
                if (lot.attributes.name) {
                    acc[parseInt(lot.attributes.name)] = this.spatialUtilService
                        .parseIdsAttr(lot.attributes.polygonids)
                        .filter((id: number) => !polygonDeletesIds.includes(id));
                }
                return acc;
            }, {})
        };
        const features: any[] = this.spatialUtilService
            .getGraphics(featureLayer)
            .filter(
                (g: any) =>
                    (g.attributes.polygonids && polygonEditsIds.some(id => g.attributes.polygonids.includes(`[${id}]`))) ||
                    (g.attributes.polylineids && polylineEditsIds.some(id => g.attributes.polylineids.includes(`[${id}]`))) ||
                    (g.attributes.burdenlots && polygonLotInfo.allBurdenedLotIds.some(id => g.attributes.burdenlots.match(`\\b${id}\\b`)))
            );

        if (features.length === 0) {
            return of([]);
        }

        const lotsWithBurdenLots: any[] = this.spatialUtilService
            .getLots()
            .filter(
                (lot: any) =>
                    lot.attributes.polygonids &&
                    features.some((f: any) => f.attributes.burdenlots && f.attributes.burdenlots.match(`\\b${lot.attributes.name}\\b`))
            );

        const interestLotInfo = {
            allBurdenedLotIds: lotsWithBurdenLots
                .map(lot => lot.attributes.name)
                .filter(v => v)
                .map(v => parseInt(v)),
            allBurdenedLotPolygonIds: lotsWithBurdenLots.reduce((acc, lot) => {
                acc.push(
                    ...this.spatialUtilService
                        .parseIdsAttr(lot.attributes.polygonids)
                        .filter(id => !acc.includes(id) && !polygonDeletesIds.includes(id))
                );
                return acc;
            }, []),
            burdenedLotPolygonIdsMap: lotsWithBurdenLots.reduce((acc, lot) => {
                if (lot.attributes.name) {
                    acc[parseInt(lot.attributes.name)] = this.spatialUtilService
                        .parseIdsAttr(lot.attributes.polygonids)
                        .filter(id => !polygonDeletesIds.includes(id));
                }
                return acc;
            }, {})
        };

        const featureChildIdsToKeepList: {feature: any; polygonIds: number[]; polylineIds: number[]}[] = features.map(f => ({
            feature: f,
            polygonIds: this.spatialUtilService.parseIdsAttr(f.attributes.polygonids).filter((id: number) => !polygonDeletesIds.includes(id)),
            polylineIds: this.spatialUtilService.parseIdsAttr(f.attributes.polylineids).filter((id: number) => !polylineDeletesIds.includes(id))
        }));

        const {polygonIdsToKeep, polylineIdsToKeep} = featureChildIdsToKeepList.reduce(
            (acc, v) => {
                acc.polygonIdsToKeep.push(...v.polygonIds.filter(id => !acc.polygonIdsToKeep.includes(id)));
                acc.polylineIdsToKeep.push(...v.polylineIds.filter(id => !acc.polylineIdsToKeep.includes(id)));
                return acc;
            },
            {polygonIdsToKeep: [], polylineIdsToKeep: []}
        );

        const getPolygonsResponse = polygonIdsToKeep.length
            ? this.spatialUtilService
                  .getPolygons()
                  .filter(
                      (p: any) => polygonIdsToKeep.includes(p.attributes.id) || interestLotInfo.allBurdenedLotPolygonIds.includes(p.attributes.id)
                  )
            : [];
        const getPolylinesResponse = polylineIdsToKeep.length
            ? this.spatialUtilService.getPolylines().filter(p => polylineIdsToKeep.includes(p.attributes.id))
            : [];

        // Replace updated polygons
        const polygons: any[] = polygonUpdates.concat(
            getPolygonsResponse
                .filter((p: any) => !polygonUpdatesIds.includes(p.attributes.id))
                .map((p: any) => new this.esriModuleService.Graphic(p.toJson()))
        );
        const polygonMap = polygons.reduce((acc, v) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        // Replace updated polylines
        const polylines: any[] = polylineUpdates.concat(
            getPolylinesResponse.filter(p => !polylineUpdatesIds.includes(p.attributes.id)).map(p => new this.esriModuleService.Graphic(p.toJson()))
        );
        const polylineMap = polylines.reduce((acc, v) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        const featureMap = featureChildIdsToKeepList.reduce((acc, v) => {
            const burdenedLotPolygonIds = this.spatialUtilService
                .parseIdsAttr(v.feature.attributes.burdenlots)
                .reduce((accBurdenedLotPolygonIds: number[], burdenedLotId: number) => {
                    if (interestLotInfo.burdenedLotPolygonIdsMap[burdenedLotId]) {
                        accBurdenedLotPolygonIds.push(
                            ...interestLotInfo.burdenedLotPolygonIdsMap[burdenedLotId].filter((id: number) => !accBurdenedLotPolygonIds.includes(id))
                        );
                    }
                    return accBurdenedLotPolygonIds;
                }, []);

            acc[v.feature.attributes.id] = {
                feature: v.feature,
                polygons: v.polygonIds.map(id => polygonMap[id]),
                polylines: v.polylineIds.map(id => polylineMap[id]),
                burdenedLotPolygons: burdenedLotPolygonIds.map((id: number) => polygonMap[id])
            };

            return acc;
        }, {});

        const updates: any[] = featureChildIdsToKeepList.map(v => {
            const burdenedLotPolygons: any[] = featureMap[v.feature.attributes.id].burdenedLotPolygons;
            const validBurdenedLotPolygons = burdenedLotPolygons.filter(p => p);
            const burdenedLotPolygonRings = validBurdenedLotPolygons.map(burdenedLotPolygon => burdenedLotPolygon.geometry.rings[0]);

            const newFeatureAttrs = Object.assign(this.windowWidgetsService.structuredClone(v.feature.attributes));
            newFeatureAttrs.polygonids = this.spatialUtilService.toIdsAttr(v.polygonIds);
            newFeatureAttrs.polylineids = this.spatialUtilService.toIdsAttr(v.polylineIds);

            let featureGeometryJson: any;
            if (v.feature.geometry && burdenedLotPolygonRings.length) {
                featureGeometryJson = v.feature.geometry;
                featureGeometryJson.rings.splice(0, featureGeometryJson.rings.length, ...burdenedLotPolygonRings);
            }

            return new this.esriModuleService.Graphic({attributes: newFeatureAttrs, geometry: featureGeometryJson || null});
        });

        const validUpdates = updates.filter(u => u);

        return this.constructionUtilService.updateFeatures(featureLayer, validUpdates);
    }

    private updateAssociatedParentFeatures(featureLayer: any, childEdits: any) {
        const fieldNames = featureLayer.fields.map((field: any) => field.name);
        const featureClass = this.spatialUtilService.getFeatureClass(featureLayer);

        const polygonUpdates: any[] = childEdits.polygon?.updates?.map((u: any) => new this.esriModuleService.Graphic(u.toJson())) || [];
        const polygonDeletes: any[] = childEdits.polygon?.deletes || [];
        const polygonUpdatesIds: number[] = polygonUpdates.map(p => p.attributes.id);
        const polygonDeletesIds: number[] = polygonDeletes.map(p => p.attributes.id);
        const polygonEditsIds: number[] = [...polygonUpdatesIds, ...polygonDeletesIds];

        const polylineUpdates: any[] = childEdits.polyline?.updates?.map((u: any) => new this.esriModuleService.Graphic(u.toJson())) || [];
        const polylineDeletes: any[] = childEdits.polyline?.deletes || [];
        const polylineUpdatesIds: number[] = polylineUpdates.map(p => p.attributes.id);
        const polylineDeletesIds: number[] = polylineDeletes.map(p => p.attributes.id);
        const polylineEditsIds: number[] = [...polylineUpdatesIds, ...polylineDeletesIds];

        const features: any[] = this.spatialUtilService
            .getGraphics(featureLayer)
            .filter(
                (f: any) =>
                    (f.attributes.polygonids && polygonEditsIds.some(id => f.attributes.polygonids.includes(`[${id}]`))) ||
                    (f.attributes.polylineids && polylineEditsIds.some(id => f.attributes.polylineids.includes(`[${id}]`)))
            );

        if (features.length === 0) {
            return of(null);
        }

        const featureChildIdsToKeepList: {feature: any; polygonIds: number[]; polylineIds: number[]}[] = features.map(f => ({
            feature: f,
            polygonIds: this.spatialUtilService.parseIdsAttr(f.attributes.polygonids).filter((id: number) => !polygonDeletesIds.includes(id)),
            polylineIds: this.spatialUtilService.parseIdsAttr(f.attributes.polylineids).filter((id: number) => !polylineDeletesIds.includes(id))
        }));

        const {polygonIdsToKeep, polylineIdsToKeep} = featureChildIdsToKeepList.reduce(
            (acc, v) => {
                acc.polygonIdsToKeep.push(...v.polygonIds.filter(id => !acc.polygonIdsToKeep.includes(id)));
                acc.polylineIdsToKeep.push(...v.polylineIds.filter(id => !acc.polylineIdsToKeep.includes(id)));
                return acc;
            },
            {polygonIdsToKeep: [], polylineIdsToKeep: []}
        );

        const getPolygonsResponse: any[] = polygonIdsToKeep.length
            ? this.spatialUtilService.getPolygons().filter((p: any) => polygonIdsToKeep.includes(p.attributes.id))
            : [];
        const getPolylinesResponse: any[] = polylineIdsToKeep.length
            ? this.spatialUtilService.getPolylines().filter((p: any) => polylineIdsToKeep.includes(p.attributes.id))
            : [];

        // Replace updated polygons
        const polygons = polygonUpdates.concat(
            getPolygonsResponse.filter(p => !polygonUpdatesIds.includes(p.attributes.id)).map(p => new this.esriModuleService.Graphic(p.toJson()))
        );
        const polygonMap = polygons.reduce((acc, v) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        // Replace updated polylines
        const polylines = polylineUpdates.concat(
            getPolylinesResponse.filter(p => !polylineUpdatesIds.includes(p.attributes.id)).map(p => new this.esriModuleService.Graphic(p.toJson()))
        );
        const polylineMap = polylines.reduce((acc, v) => {
            acc[v.attributes.id] = v;
            return acc;
        }, {});

        const featureMap = featureChildIdsToKeepList.reduce((acc, v) => {
            acc[v.feature.attributes.id] = {
                feature: v.feature,
                polygons: v.polygonIds.map(id => polygonMap[id]),
                polylines: v.polylineIds.map(id => polylineMap[id])
            };
            return acc;
        }, {});

        const updates: any[] = featureChildIdsToKeepList.map(v => {
            const featurePolygons: any[] = featureMap[v.feature.attributes.id].polygons;
            const featurePolygonRings = featurePolygons.map(featurePolygon => featurePolygon.geometry.rings[0]);

            const newFeatureAttrs = Object.assign(this.windowWidgetsService.structuredClone(v.feature.attributes));

            if (fieldNames.includes('polygonids')) {
                newFeatureAttrs.polygonids = this.spatialUtilService.toIdsAttr(v.polygonIds);
            }

            if (fieldNames.includes('totalarea')) {
                newFeatureAttrs.totalarea = featurePolygons.reduce((acc: number, p: any) => acc + parseFloat(p.attributes.statedarea), 0);
            }

            if (fieldNames.includes('polylineids')) {
                newFeatureAttrs.polylineids = this.spatialUtilService.toIdsAttr(v.polylineIds);
            }

            let featureGeometryJson: any;
            if (newFeatureAttrs.polygonids) {
                if (featurePolygonRings.length === 0) {
                    featureGeometryJson = v.feature.geometry;
                    featureGeometryJson.rings.length = 0;
                } else {
                    featureGeometryJson = this.spatialUtilService
                        .polygonGeometryUnion(
                            featurePolygonRings.map((ring: any) => {
                                const geometry = v.feature.geometry.toJson();
                                geometry.rings.splice(0, geometry.rings.length, ring);
                                return new this.esriModuleService.geometry.Polygon(geometry);
                            })
                        )
                        .toJson();
                }
            }

            return new this.esriModuleService.Graphic({attributes: newFeatureAttrs, geometry: featureGeometryJson || null});
        });

        let observable = this.constructionUtilService.updateFeatures(featureLayer, updates);

        if (polygonEditsIds.length) {
            const updatesWithPolygons = updates.filter(u => featureMap[u.attributes.id].polygons.length);
            observable = observable.pipe(
                switchMap(() =>
                    forkJoinDefaultIfEmpty(
                        updatesWithPolygons.map(f => {
                            const featurePolygons: any[] = featureMap[f.attributes.id].polygons;
                            return this.annotationService.update(f, featureClass, {polygons: featurePolygons, updateGeometry: false}).pipe(
                                switchMap(() =>
                                    forkJoinDefaultIfEmpty(
                                        featurePolygons.map(p => {
                                            f.attributes['polygon.statedarea'] = p.attributes.statedarea;
                                            p.attributes['polygon.statedarea'] = p.attributes.statedarea;
                                            return this.annotationService.update(p, 'polygon', {
                                                polygons: featurePolygons,
                                                childPolygon: p,
                                                updateGeometry: false
                                            });
                                        })
                                    )
                                )
                            );
                        })
                    )
                )
            );
        }

        return observable;
    }

    private updateAssociatedPolygons(linesToDelete: any[], updatedLine: any, unselectedMiddlePointLines: any[]) {
        const linesToDeleteIds = linesToDelete.map(l => l.attributes.id);
        const unselectedMiddlePointLineIds = unselectedMiddlePointLines.map(l => l.attributes.id);

        const allLineIds = [...linesToDeleteIds, ...unselectedMiddlePointLineIds, updatedLine.attributes.id];

        const polygons = this.spatialUtilService
            .getPolygons()
            .filter((p: any) => p.attributes.lineids && allLineIds.some(lineId => p.attributes.lineids.includes(`[${lineId}]`)));

        const edits = polygons.reduce(
            (acc: {updates: any[]; deletes: any[]}, p: any) => {
                const polygonLineIds: number[] = this.spatialUtilService.parseIdsAttr(p.attributes.lineids);
                if (polygonLineIds.some(polygonLineId => unselectedMiddlePointLineIds.includes(polygonLineId))) {
                    acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('polygon', p));
                    return acc;
                }

                const linesToKeepIds = polygonLineIds.filter(id => !linesToDeleteIds.includes(id));

                // add the new line id to the lines to keep ids
                linesToKeepIds.push(updatedLine.attributes.id);
                const linesToKeep = this.spatialUtilService.getLines().filter((l: any) => linesToKeepIds.includes(l.attributes.id));

                // check if the lines form a closed polygon
                const polygonRingInfo = this.polygonUtilService.getPolygonRing(linesToKeep, undefined);

                if (polygonRingInfo.ring && polygonRingInfo.ring.length > 3) {
                    const polygonGeometryJson = p.geometry.toJson();
                    polygonGeometryJson.rings[0] = polygonRingInfo.ring.map(point => [point.x, point.y]);

                    const newPolygonAttrs = Object.assign(this.windowWidgetsService.structuredClone(p.attributes), {
                        lineids: polygonRingInfo.lineids
                    });
                    acc.updates.push(new this.esriModuleService.Graphic({attributes: newPolygonAttrs, geometry: polygonGeometryJson}));
                } else {
                    acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('polygon', p));
                }

                return acc;
            },
            {updates: [], deletes: []}
        );

        let observable = edits.deletes.length
            ? this.constructionUtilService.deleteFeatures(this.spatialUtilService.getPolygonMapLayer(), edits.deletes)
            : of(null);

        if (edits.updates.length) {
            const polygonAreas = this.spatialUtilService.geodesicAreas(edits.updates.map((p: any) => p.geometry));
            polygonAreas.forEach((area: number, areaIndex: number) => (edits.updates[areaIndex].attributes.statedarea = `${Math.round(area)}`));
            observable = observable.pipe(
                switchMap(() => this.constructionUtilService.updateFeatures(this.spatialUtilService.getPolygonMapLayer(), edits.updates)),
                switchMap(() =>
                    forkJoinDefaultIfEmpty(edits.updates.map((p: any) => this.annotationService.update(p, 'polygon', {updateGeometry: false})))
                )
            );
        }

        return observable.pipe(map(() => edits));
    }

    private updateAssociatedLines(linesToDelete: any[], endPtIds: any[], newLineAttrs: any) {
        const startAndEndPts = this.getPointsByType(endPtIds);

        if (startAndEndPts.length !== 2) {
            return of(null);
        }

        const startPoint = startAndEndPts[0];
        const endPoint = startAndEndPts[1];

        const attrs = this.populateLineAttributes(startPoint, endPoint, linesToDelete, newLineAttrs);

        const line = this.constructionUtilService.createLineGraphic(startPoint, endPoint, attrs);
        return this.spatialUtilService.applyEdits('line', [line], null, null).pipe(
            switchMap(() => this.annotationService.update(line, 'line', {updateGeometry: true, createAnnotation: true})),
            map(() => this.spatialUtilService.getLines().find((l: any) => l.attributes.id === line.attributes.id))
        );
    }

    private populateLineAttributes(startPoint: any, endPoint: any, linesToDelete: any, newLineAttrs: any) {
        const attrs = this.spatialUtilService.deleteAttrs(linesToDelete[0].toJson().attributes, ['id', 'bearing']);

        Object.assign(attrs, newLineAttrs, {id: this.spatialSequenceService.getLineSequence(1)[0]});

        if (!attrs.bearing) {
            attrs.bearing = this.angleUtilService.calcBearingDms(
                startPoint.geometry.x,
                startPoint.geometry.y,
                endPoint.geometry.x,
                endPoint.geometry.y
            );
        }

        attrs.distance = typeof attrs.distance === 'number' ? parseFloat(attrs.distance.toFixed(3)) : this.initDistanceValue();
        attrs.frompointid = startPoint.attributes.id;
        attrs.topointid = endPoint.attributes.id;

        return attrs;
    }

    private updateAssociatedPolyLines(endPtIds: any[], middlePtIds: any[], connectedPtsIds: any[]) {
        const startAndEndPts = this.getPointsByType(endPtIds);

        if (startAndEndPts.length !== 2) {
            return of(null);
        }

        const deletesAndUpdates = this.getUpdatedAssociatedPolyLines(middlePtIds, connectedPtsIds);

        return this.constructionUtilService.deleteFeatures(this.spatialUtilService.getPolylineMapLayer(), deletesAndUpdates.deletes).pipe(
            switchMap(() => this.constructionUtilService.updateFeatures(this.spatialUtilService.getPolylineMapLayer(), deletesAndUpdates.updates)),
            map(() => deletesAndUpdates)
        );
    }

    private getUpdatedAssociatedPolyLines(middlePtIds: any[], connectedPtsIds: any[]) {
        const polyLines = this.spatialUtilService
            .getPolylines()
            .filter(p => this.spatialUtilService.parseIdsAttr(p.attributes.pointids).some((id: number) => connectedPtsIds.includes(id)));

        if (polyLines && polyLines.length === 0) {
            return {deletes: [], updates: []};
        }

        const {deletes, updates} = polyLines.reduce(
            (accEdits, polyline) => {
                const newAttrs = this.windowWidgetsService.structuredClone(polyline.attributes);
                const geometryJson = polyline.geometry.toJson();
                const polyLinePoints = this.spatialUtilService.parseIdsAttr(polyline.attributes.pointids);
                const polyLineEndPts: any[] = polyLinePoints.reduce((acc: any[], v: any, i: number) => {
                    if (i === 0 || i === polyLinePoints.length - 1) {
                        acc.push(v);
                    }
                    return acc;
                }, []);

                // if one of the polyline end points is a middle point of the lines to be merged
                // then delete this polyline
                const polylinePtInMiddle = middlePtIds.filter(m => polyLineEndPts.some(p => p === m))[0];
                if (polylinePtInMiddle) {
                    accEdits.deletes.push(this.spatialUtilService.toApplyEditsDelete('polyline', polyline));
                } else {
                    // the polyline spans all the selected lines to be merged
                    // remove the middle points from the polyline points ids and geometry
                    const {path, pointIds} = geometryJson.paths[0]
                        .map((v: any, i: number) => ({id: polyLinePoints[i], value: v}))
                        .filter((v: any) => !middlePtIds.includes(v.id))
                        .reduce(
                            (acc: {path: any[]; pointIds: number[]}, v: any) => {
                                acc.path.push(v.value);
                                acc.pointIds.push(v.id);
                                return acc;
                            },
                            {path: [], pointIds: []}
                        );

                    newAttrs.pointids = this.spatialUtilService.toIdsAttr(pointIds);
                    geometryJson.paths[0] = path;
                    accEdits.updates.push(new this.esriModuleService.Graphic({attributes: newAttrs, geometry: geometryJson}));
                }

                return accEdits;
            },
            {updates: [], deletes: []}
        );

        return {deletes, updates};
    }

    private updateTotalLotBoundaries(linesToDelete: any[], endPtIds: any[], connectedPtsIds: any[], createdLine: any) {
        const startAndEndPts = this.getPointsByType(endPtIds);

        if (startAndEndPts.length !== 2) {
            return of(null);
        }

        const deletesAndUpdates = this.getAssociatedTotalLotBoundariesToUpdate(
            linesToDelete,
            startAndEndPts[0],
            startAndEndPts[1],
            connectedPtsIds,
            createdLine
        );

        return this.constructionUtilService.deleteFeatures(this.spatialUtilService.getLineMapLayer(), deletesAndUpdates.deletes).pipe(
            switchMap(() => this.constructionUtilService.updateFeatures(this.spatialUtilService.getLineMapLayer(), deletesAndUpdates.updates)),
            switchMap(() =>
                forkJoinDefaultIfEmpty(deletesAndUpdates.updates.map(u => this.annotationService.update(u, 'line', {updateGeometry: true})))
            )
        );
    }

    private getAssociatedTotalLotBoundariesToUpdate(
        linesToDelete: any[],
        startPoint: any,
        endPoint: any,
        connectedPtsIds: number[],
        mergedLine: any
    ) {
        const linesToDeleteIds: number[] = linesToDelete.map(l => l.attributes.id);
        const totalLotBoundaryLookup = this.getLineTypeLookups().find(l => l.businessClass === 'TotalLotBoundary');
        const startPointId = startPoint.attributes.id;
        const endPointId = endPoint.attributes.id;

        const lines: any[] = this.spatialUtilService
            .getLines()
            .filter((l: any) =>
                l.attributes.lineids ? l.attributes.type === totalLotBoundaryLookup.id : l.attributes.frompointid && l.attributes.topointid
            );

        const tlbInfoList = lines
            .filter(l => l.attributes.lineids)
            .map(tlb => {
                const lineIds: number[] = this.spatialUtilService.parseIdsAttr(tlb.attributes.lineids);

                const pointIds = lineIds
                    .map(id => lines.find(l => l.attributes.id === id))
                    .reduce((acc, v, i, a) => {
                        const {frompointid: fromPointId, topointid: toPointId} = v.attributes;
                        if (i === 0) {
                            acc.push(
                                a[1] && [a[1].attributes.frompointid, a[1].attributes.topointid].includes(fromPointId) ? toPointId : fromPointId
                            );
                        }
                        acc.push(acc.includes(fromPointId) ? toPointId : fromPointId);
                        return acc;
                    }, []);

                return {tlb: tlb, lineIds: lineIds, pointIds: pointIds};
            })
            .filter(tlbInfo => tlbInfo.pointIds.includes(startPointId) || tlbInfo.pointIds.includes(endPointId));

        return tlbInfoList.reduce(
            (acc, tlbInfo) => {
                const tlb = tlbInfo.tlb;
                const tlbLinesIds = tlbInfo.lineIds;
                const tlbRemainderLineIds = tlbLinesIds.filter(id => !linesToDeleteIds.includes(id));

                //if the TLB lines ids and the TLB remainder line ids are the same the TLB is not impacted by this merge
                const sortedTLBLinesIds = tlbLinesIds.sort();
                const sortedTLBRemainderLineIds = tlbRemainderLineIds.sort();

                const areEqual = sortedTLBLinesIds.reduce((acc, v, i) => {
                    if (sortedTLBRemainderLineIds.indexOf(v) !== i) {
                        acc.push(false);
                    }
                    return acc;
                }, []);

                // if the arrays are equal then do nothing and return
                if (areEqual.length === 0) {
                    return {deletes: [], updates: []};
                }

                // get the lines that represent the TLB
                const tlbLines = this.spatialUtilService.getLines().filter((l: any) => tlbLinesIds.some(id => id === l.attributes.id));
                const tlbConnectedPtIds = this.getAllConnectedPointIds(tlbLines);
                const tlbIntermediatePtIds = this.getMiddlePoints(tlbConnectedPtIds);

                // Delete total lot boundary if:
                // there are no intermediate points in the TLB that match either the start or end pt of the merged line
                if (!tlbIntermediatePtIds.includes(startPointId) && !tlbIntermediatePtIds.includes(endPointId)) {
                    acc.deletes.push(this.spatialUtilService.toApplyEditsDelete('line', tlb));
                } else if (tlbRemainderLineIds.length < tlbLinesIds.length) {
                    const tlbUpdate = new this.esriModuleService.Graphic(tlb.toJson());

                    // check if all the connected pt ids (for the lines to be merged) are in the TLB point ids
                    if (connectedPtsIds.every(v => tlbInfo.pointIds.includes(v))) {
                        // just update the line Ids and not the geometry
                        if (mergedLine) {
                            tlbUpdate.attributes.lineids = [...tlbRemainderLineIds, mergedLine.attributes.id].map(id => `[${id}]`).join();
                        }
                    } else {
                        // find the start and end Pts of the TLB and extend the TLB to match the merged lines
                        const tlbStartPt = this.getTLBStartPt(connectedPtsIds, tlbInfo.pointIds, tlb);
                        const combinedLines = [...tlbLines, ...linesToDelete];
                        const allConnectedPtIds = this.getAllConnectedPointIds(combinedLines);
                        const endPtIds = this.getStartAndEndPointIds(allConnectedPtIds);
                        const tlbExtendedPt = this.getTLBExtendedPt(tlbStartPt, endPtIds);

                        tlbUpdate.attributes.frompointid = tlbStartPt.attributes.id;
                        tlbUpdate.attributes.topointid = tlbExtendedPt.attributes.id;

                        //Note: Calculate distance using distance calculation method settings
                        tlbUpdate.attributes.distance = this.constructionUtilService.getDistanceBetweenPoints(tlbStartPt, tlbExtendedPt);
                        if (mergedLine) {
                            tlbUpdate.attributes.lineids = [...tlbRemainderLineIds, mergedLine.attributes.id].map(id => `[${id}]`).join();
                        }

                        tlbUpdate.geometry.paths[0][0][0] = tlbStartPt.geometry.x;
                        tlbUpdate.geometry.paths[0][0][1] = tlbStartPt.geometry.y;

                        tlbUpdate.geometry.paths[0][1][0] = tlbExtendedPt.geometry.x;
                        tlbUpdate.geometry.paths[0][1][1] = tlbExtendedPt.geometry.y;
                    }

                    acc.updates.push(tlbUpdate);
                }

                return acc;
            },
            {deletes: [], updates: []}
        );
    }

    private getTLBExtendedPt(tlbExtendedPt: any, endPtIds: number[]) {
        return this.getPointsByType(endPtIds.filter(p => p !== tlbExtendedPt.attributes.id))[0];
    }

    private getTLBStartPt(connectedPtsIds: number[], tlbPointIds: number[], tlb: any) {
        const remainderPtIds = tlbPointIds.reduce((acc, v) => {
            if (!connectedPtsIds.includes(v)) {
                acc.push(v);
            }

            return acc;
        }, []);

        const id =
            (remainderPtIds.includes(tlb.attributes.frompointid) && tlb.attributes.frompointid) ||
            (remainderPtIds.includes(tlb.attributes.topointid) && tlb.attributes.topointid);

        return id ? this.getPointsByType([id])[0] : undefined;
    }

    private updateAssociatedAngles(middlePtIds: number[], endPtIds: number[], linesToDeleteIds: number[], createdLine: any) {
        const middlePtAngles: any[] = this.spatialUtilService.getAngles().filter((a: any) => middlePtIds.some(p => p === a.attributes.pointid));

        if (middlePtAngles.length) {
            // delete the angles not associated with the end pts of the selected lines
            return this.constructionUtilService.deleteFeatures(this.spatialUtilService.getAngleMapLayer(), middlePtAngles).pipe(
                switchMap(() => {
                    // update the angle fromLineId and toLineId attributes on all the angles that reference the end points
                    const createdLineId = createdLine.attributes.id;

                    const startAndEndLines: any[] = this.spatialUtilService
                        .getLines()
                        .filter((l: any) => endPtIds.includes(l.attributes.frompointid) || endPtIds.includes(l.attributes.topointid));

                    const anglesToUpdate: any[] = this.spatialUtilService
                        .getAngles()
                        .filter(
                            (a: any) =>
                                startAndEndLines.some(l => [a.attributes.anglefromlineid, a.attributes.angletolineid].includes(l.attributes.id)) &&
                                linesToDeleteIds.some(id => [a.attributes.anglefromlineid, a.attributes.angletolineid].includes(id))
                        );

                    anglesToUpdate.forEach(a => {
                        if (linesToDeleteIds.includes(a.attributes.anglefromlineid)) {
                            a.attributes.anglefromlineid = createdLineId;
                        }
                        if (linesToDeleteIds.includes(a.attributes.angletolineid)) {
                            a.attributes.angletolineid = createdLineId;
                        }
                    });

                    return this.spatialUtilService.applyEdits('angle', null, anglesToUpdate, null);
                })
            );
        }

        return of(null);
    }

    private getConnectedLines(): any[] {
        return this.getSelectedMergeableLines().reduce((acc: any[], v: any, i: number, a: any[]) => {
            if (acc.length === 0) {
                acc.push(v);
            }
            acc.push(
                ...a.filter(
                    line =>
                        !acc.includes(line) &&
                        acc.some(connectedLine => {
                            const linePointIds = [line.attributes.frompointid, line.attributes.topointid];
                            return (
                                linePointIds.includes(connectedLine.attributes.frompointid) ||
                                linePointIds.includes(connectedLine.attributes.topointid)
                            );
                        })
                )
            );
            return acc;
        }, []);
    }

    private getAllConnectedPointIds(linesToDelete: any[]) {
        // remove duplicate lines
        const uniqueLinesToDelete: any[] = linesToDelete.reduce((acc, v) => {
            if (!acc.includes(v)) {
                acc.push(v);
            }
            return acc;
        }, []);

        return uniqueLinesToDelete.reduce((acc, v) => {
            acc.push(v.attributes.frompointid);
            acc.push(v.attributes.topointid);

            return acc;
        }, []);
    }

    private getUniqueConnectedPtIds(allConnectedPtIds: number[]) {
        return allConnectedPtIds.reduce((acc, v) => {
            if (!acc.includes(v)) {
                acc.push(v);
            }

            return acc;
        }, []);
    }

    private getStartAndEndPointIds(allConnectedPtIds: number[]) {
        // for each connected point id - find out if the id occurs only once - if so these are the end points
        const connectedPts = allConnectedPtIds.reduce((acc, v) => {
            if (v in acc) {
                acc[v]++;
            } else {
                acc[v] = 1;
            }
            return acc;
        }, []);

        const startAndEndPts = [];
        connectedPts.filter((v, i) => {
            if (v === 1) {
                startAndEndPts.push(i);
            }
        });

        return this.bearingReversed ? startAndEndPts.reverse() : startAndEndPts;
    }

    private getMiddlePoints(allConnectedPtIds: number[]) {
        // for each connected point id - find out if the id occurs more than once - if so these are the middle points
        const connectedPts = allConnectedPtIds.reduce((acc, v) => {
            if (v in acc) {
                acc[v]++;
            } else {
                acc[v] = 1;
            }
            return acc;
        }, []);

        const middlePoints = [];
        connectedPts.filter((v, i) => {
            if (v > 1) {
                middlePoints.push(i);
            }
        });
        return middlePoints;
    }

    private getPointsByType(pointIds: number[]) {
        const pointTypeLookup = this.getPointTypeLookups();
        const controlPointTypeLookup = this.getControlPointTypeLookups();
        const points = [];

        // get the points
        pointIds.map(p => {
            let point = this.spatialUtilService.getPoints().find((point: any) => point.attributes.id === p);
            if (point && pointTypeLookup.some(pointType => point.attributes.type === pointType.id && pointType)) {
                points.push(point);
            }

            point = this.spatialUtilService.getControlPoints().find((point: any) => point.attributes.id === p);
            if (point && controlPointTypeLookup.some(pointType => point.attributes.type === pointType.id && pointType)) {
                points.push(point);
            }
        });

        return points;
    }

    private validate() {
        console.debug(`${this.title} - validate`);
        const selectedLines = this.getSelectedMergeableLines();

        const tool = this.getTool();

        tool.errors.length = 0;
        tool.warnings.length = 0;

        let message = '';

        if (this.noLineSelectedCheck(selectedLines)) {
            tool.errors.push(tool.errorMessage1);
            return;
        }

        if (this.singleLineSelectedCheck(selectedLines)) {
            tool.errors.push(tool.errorMessage2);
            return;
        }

        const points = this.pointsCount(selectedLines);

        if (this.openTraverseCheck(points, selectedLines)) {
            tool.errors.push(tool.errorMessage3);
        }

        if (this.closedTraverseCheck(points, selectedLines)) {
            tool.errors.push(tool.errorMessage4);
        }

        if (this.isSpurLineCheck(points)) {
            message = tool.errorMessage5.replace(/\[(.+?)]/, this.spurLineErrorMessage(points));
            tool.errors.push(message);
        }

        const businessCheck = this.businessClassCheck(selectedLines);
        if (businessCheck.notPermitted.length) {
            const businessClassesHtml = businessCheck.businessClasses.map(bc => '\n' + `${bc}`).join(' ');
            message = tool.errorMessage6.replace('[0]', businessClassesHtml);
            tool.errors.push(message);
        }

        const bearingValueCheck = this.bearingValueThresholdCheck();
        if (bearingValueCheck.result) {
            message = tool.warningMessage1.replace('[0]', this.getBearingValue()).replace('[1]', this.getGeometryBearing());
            tool.warnings.push(message);
        }

        const polygonDeletionWarning = this.polygonDeletionCheck();
        if (polygonDeletionWarning) {
            message = tool.warningMessage2.replace(/\[(.+?)]/, polygonDeletionWarning);
            tool.warnings.push(message);
        }

        const totalLotBoundaryDeletion = this.totalLotBoundaryLineDeletionCheck();
        if (totalLotBoundaryDeletion.result === true) {
            message = tool.warningMessage3.replace(/\[(.+?)]/, totalLotBoundaryDeletion.totalLots);
            tool.warnings.push(message);
        }

        const polylineDeletionWarning = this.polylineDeletionCheck();
        if (polylineDeletionWarning) {
            message = tool.warningMessage4.replace(/\[(.+?)]/, polylineDeletionWarning);
            tool.warnings.push(message);
        }
    }

    private pointsCount(selectedLines: any[]) {
        return selectedLines.reduce((acc, v) => {
            acc[v.attributes.frompointid] = (acc[v.attributes.frompointid] || 0) + 1;
            acc[v.attributes.topointid] = (acc[v.attributes.topointid] || 0) + 1;
            return acc;
        }, {});
    }

    private filterDisconnectedPoints(points: any) {
        return Object.keys(points).reduce((acc, k) => {
            if (points[k] !== 2) {
                acc[k] = parseInt(points[k]);
            }
            return acc;
        }, {});
    }

    private filterDisconnectedLines(points: any, selectedLines: any[]) {
        return Object.keys(points).reduce((acc, k) => {
            if (points[k] === 1) {
                const index = acc.findIndex(f => String(f.attributes.frompointid) === String(k) || String(f.attributes.topointid) === String(k));
                if (index > 0) {
                    acc.splice(index, 1);
                }
            }
            return acc;
        }, selectedLines.slice());
    }

    private noLineSelectedCheck(selectedLines: any[]) {
        return selectedLines && selectedLines.length === 0;
    }

    private singleLineSelectedCheck(selectedLines: any[]) {
        return selectedLines && selectedLines.length === 1;
    }

    private closedTraverseCheck(points: any, selectedLines: any[]) {
        const filteredLines = this.filterDisconnectedLines(points, selectedLines);

        const filteredPointsCount = this.pointsCount(filteredLines);

        const filteredPoints = this.filterDisconnectedPoints(filteredPointsCount);

        return Object.keys(filteredPoints).length === 0 && selectedLines.length > 0;
    }

    private openTraverseCheck(points: any, selectedLines: any[]) {
        const filteredPoints = this.filterDisconnectedPoints(points);

        if (Object.keys(filteredPoints).length !== 2) {
            const openEnds = Object.values(filteredPoints).filter(i => i === 1);
            return openEnds.length !== 2;
        } else if (this.closedTraverseCheck(points, selectedLines)) {
            return true;
        }

        return false;
    }

    private isSpurLineCheck(points: any) {
        const filteredPoints = this.filterDisconnectedPoints(points);
        return Object.values(filteredPoints).some((v: number) => v > 2);
    }

    private spurLineErrorMessage(points: any) {
        if (this.isSpurLineCheck(points)) {
            const filteredPoints = this.filterDisconnectedPoints(points);
            const spurPoints = Object.keys(filteredPoints).reduce((acc, k) => {
                if (filteredPoints[k] > 2) {
                    acc[k] = filteredPoints[k];
                }
                return acc;
            }, {});
            return Object.keys(spurPoints).join(',');
        }
        return '';
    }

    private businessClassCheck(selectedLines: any[]): {businessClasses: string[]; permitted: string[]; notPermitted: string[]} {
        return selectedLines.reduce(
            (acc: {businessClasses: string[]; permitted: string[]; notPermitted: string[]}, line: any) => {
                const lineTypeLookup = this.getLineTypeLookups().find(l => l.id === line.attributes.type);
                if (lineTypeLookup) {
                    const businessClass = lineTypeLookup.businessClass;

                    if (!acc.businessClasses.includes(businessClass)) {
                        acc.businessClasses.push(businessClass);
                        acc.businessClasses.sort();
                    }

                    const permitMergeBusinessClasses = lineTypeLookup.permitMergeBusinessClasses;

                    if (permitMergeBusinessClasses.length === 0) {
                        acc.notPermitted.push(businessClass);
                    } else {
                        if (acc.permitted.length === 0) {
                            acc.permitted.push(...permitMergeBusinessClasses);
                        } else if (!acc.notPermitted.includes(businessClass) && !acc.permitted.some(p => permitMergeBusinessClasses.includes(p))) {
                            acc.notPermitted.push(businessClass);
                        }
                    }
                }
                return acc;
            },
            {businessClasses: [], permitted: [], notPermitted: []}
        );
    }

    private bearingValueThresholdCheck() {
        const startEndPoints = this.getEndPoints();
        const bearingCheck = {result: false};
        if (startEndPoints && startEndPoints.length === 2) {
            const startPoint = this.getPointsByType([startEndPoints[0]])[0];
            const endPoint = this.getPointsByType([startEndPoints[1]])[0];

            const bearing = this.angleUtilService.calcBearing(startPoint.geometry.x, startPoint.geometry.y, endPoint.geometry.x, endPoint.geometry.y);

            // Check that the entered offset distance is less than the geometry distance of the line being dissected:

            const bearingAttr = this.attributes.find(a => a.prop === 'bearing');
            if (bearingAttr && bearingAttr.value) {
                const bearingDD = this.angleUtilService.dmsToDd(bearingAttr.value);

                if (!isNaN(bearingDD) && isFinite(bearingDD)) {
                    if (Math.abs(bearingDD - bearing) > 0.00555556) {
                        bearingCheck.result = true;
                    }
                }
            }
        }
        return bearingCheck;
    }

    private getBearingValue() {
        const bearingAttr = this.attributes.find(a => a.prop === 'bearing');
        return (bearingAttr && bearingAttr.value) || 0;
    }

    private getGeometryBearing() {
        const startEndPoints = this.getEndPoints();
        if (startEndPoints && startEndPoints.length === 2) {
            const startPoint = this.getPointsByType([startEndPoints[0]])[0];
            const endPoint = this.getPointsByType([startEndPoints[1]])[0];
            const bearing = this.angleUtilService.calcBearing(startPoint.geometry.x, startPoint.geometry.y, endPoint.geometry.x, endPoint.geometry.y);
            return this.angleUtilService.ddToDms(bearing);
        }
        return 0;
    }

    private polygonDeletionCheck() {
        const linesToDelete = this.getConnectedLines();
        const linesToDeleteIds = linesToDelete.map(l => l.attributes.id);

        const polygons = this.spatialUtilService.getPolygons().filter((polygon: any) => {
            const polygonLineIds = this.spatialUtilService.parseIdsAttr(polygon.attributes.lineids);
            return linesToDelete.some(l => polygonLineIds.includes(l.attributes.id));
        });

        const allConnectedPtIds = this.getAllConnectedPointIds(linesToDelete);
        const middlePtIds = this.getMiddlePoints(allConnectedPtIds);

        const linesAttachedToPoints = this.spatialUtilService
            .getLines()
            .filter((l: any) => middlePtIds.some(p => p === l.attributes.frompointid || p === l.attributes.topointid));
        const polygonsAttachedToPoints = this.spatialUtilService.getPolygons().filter((p: any) => {
            const polygonLineIds = this.spatialUtilService.parseIdsAttr(p.attributes.lineids);
            return linesAttachedToPoints.some((l: any) => polygonLineIds.includes(l.attributes.id));
        });

        const allPolygons = [...polygons, ...polygonsAttachedToPoints];
        const uniquePolygonsAttachedPoints = allPolygons.reduce((acc, v) => {
            if (!polygons.includes(v)) {
                acc.push(v);
            }
            return acc;
        }, []);

        const polygonsToDelete = [];
        const message = {};

        polygons.forEach((p: any) => {
            const polygonLineIds: number[] = this.spatialUtilService.parseIdsAttr(p.attributes.lineids);
            const polygonInterestIds: number[] = this.spatialUtilService.parseIdsAttr(p.attributes.interestids);

            message[p.attributes.id] = {lots: [], roads: [], interests: []};

            if (
                polygonLineIds.some(polygonLineId => linesToDeleteIds.includes(polygonLineId)) &&
                !linesToDeleteIds.every(lineId => polygonLineIds.includes(lineId))
            ) {
                polygonsToDelete.push(p);

                message[p.attributes.id].lots.push(
                    ...this.spatialUtilService
                        .getLots()
                        .filter((lot: any) => lot.attributes.id === p.attributes.lotid)
                        .map((lot: any) => lot.attributes.name)
                );

                message[p.attributes.id].roads.push(
                    ...this.spatialUtilService
                        .getRoads()
                        .filter((road: any) => road.attributes.id === p.attributes.roadid)
                        .map((road: any) => `${road.attributes.name} ${road.attributes.typecode} ${road.attributes.suffix}`)
                );

                message[p.attributes.id].interests.push(
                    ...this.spatialUtilService
                        .getInterests()
                        .filter((interest: any) => polygonInterestIds.includes(interest.attributes.id))
                        .map((interest: any) => interest.attributes.subject)
                );
            }
        });

        uniquePolygonsAttachedPoints.forEach((p: any) => {
            const polygonInterestIds = this.spatialUtilService.parseIdsAttr(p.attributes.interestids);

            message[p.attributes.id] = {lots: [], roads: [], interests: []};

            polygonsToDelete.push(p);

            message[p.attributes.id].lots.push(
                ...this.spatialUtilService
                    .getLots()
                    .filter((lot: any) => lot.attributes.id === p.attributes.lotid)
                    .map((lot: any) => lot.attributes.name)
            );

            message[p.attributes.id].roads.push(
                ...this.spatialUtilService
                    .getRoads()
                    .filter((road: any) => road.attributes.id === p.attributes.roadid)
                    .map((road: any) => `${road.attributes.name} ${road.attributes.typecode} ${road.attributes.suffix}`)
            );

            message[p.attributes.id].interests.push(
                ...this.spatialUtilService
                    .getInterests()
                    .filter((interest: any) => polygonInterestIds.includes(interest.attributes.id))
                    .map((interest: any) => interest.attributes.subject)
            );
        });

        const legalParcelTypeLookups = this.getLegalParcelTypeLookups();

        const polygonsToDeleteSorted = polygonsToDelete.reduce((acc: any[], v: any) => {
            v.attributes['description'] = legalParcelTypeLookups.find(l => l.id === v.attributes.type).description;
            acc.push(v);
            acc.sort((a, b) => {
                if (a.attributes.description === b.attributes.description) {
                    return a.attributes.id - b.attributes.id;
                }
                return a.attributes.description < b.attributes.description ? -1 : 1;
            });
            return acc;
        }, []);

        return polygonsToDeleteSorted.length
            ? polygonsToDeleteSorted.reduce(
                  (acc: string, v: any) =>
                      `${acc}\n${v.attributes.description} Polygon [${v.attributes.id}]\n` +
                      `Lots: ${message[v.attributes.id].lots.join(',')}\n` +
                      `Roads: ${message[v.attributes.id].roads.join(',')}\n` +
                      `Interest Subject Ref(s): ${message[v.attributes.id].interests.join(',')}`,
                  ''
              )
            : '';
    }

    private totalLotBoundaryLineDeletionCheck() {
        const totalLotBoundaryLineDeletion = {totalLots: '', result: false};

        const linesToDelete = this.getConnectedLines();
        const linesToDeleteIds = linesToDelete.map(l => l.attributes.id);

        if (linesToDeleteIds.length === 1 || this.getEndPoints().length !== 2) {
            return totalLotBoundaryLineDeletion;
        }

        const allConnectedPtIds = this.getAllConnectedPointIds(linesToDelete);
        const connectedPtsIds = this.getUniqueConnectedPtIds(allConnectedPtIds);
        const endPtIds = this.getStartAndEndPointIds(allConnectedPtIds);
        const middlePtIds = this.getMiddlePoints(allConnectedPtIds);
        const startAndEndPts = this.getPointsByType(endPtIds);

        const totalLotBoundaryLookup = this.getLineTypeLookups().find(l => l.businessClass === 'TotalLotBoundary');

        const deletesAndUpdates = this.getAssociatedTotalLotBoundariesToUpdate(
            linesToDelete,
            startAndEndPts[0],
            startAndEndPts[1],
            connectedPtsIds,
            undefined
        );

        const tlbInfoList: any[] = this.spatialUtilService
            .getLines()
            .filter(
                (l: any) =>
                    l.attributes.lineids &&
                    (deletesAndUpdates.deletes.some((d: any) => d.attributes.id === l.attributes.id) ||
                        (!linesToDeleteIds.includes(l.attributes.id) &&
                            middlePtIds.some(id => [l.attributes.frompointid, l.attributes.topointid].includes(id))))
            );

        const tlbInfoSorted = tlbInfoList.sort((a, b) => a.attributes.id - b.attributes.id);

        if (tlbInfoSorted.length) {
            totalLotBoundaryLineDeletion.totalLots = tlbInfoSorted.reduce(
                (acc: string, v: any) =>
                    `${acc}\n${totalLotBoundaryLookup.description} [${v.attributes.id}] | Distance ${parseFloat(v.attributes.distance).toFixed(2)}m`,
                ''
            );
        }

        totalLotBoundaryLineDeletion.result = totalLotBoundaryLineDeletion.totalLots.length > 0;

        return totalLotBoundaryLineDeletion;
    }

    private checkDeletePolyline(selectedLines: any[], pointIds: number[], startEndPoint: number) {
        const result = true;
        const line = selectedLines.filter(f => f.attributes.frompointid === startEndPoint || f.attributes.topointid === startEndPoint);
        if (line.length > 0) {
            if (line[0].attributes.frompointid === startEndPoint) {
                if (pointIds.includes(line[0].attributes.topointid)) {
                    if (pointIds[0] !== line[0].attributes.topointid && pointIds[pointIds.length - 1] !== line[0].attributes.topointid) {
                        return false;
                    } else {
                        if (line[0].attributes.topointid !== startEndPoint || pointIds[pointIds.length - 1] !== startEndPoint) {
                            return true;
                        }
                    }
                }
            }

            if (line[0].attributes.topointid === startEndPoint) {
                if (pointIds.includes(line[0].attributes.frompointid)) {
                    if (pointIds[0] !== line[0].attributes.frompointid && pointIds[pointIds.length - 1] !== line[0].attributes.frompointid) {
                        return false;
                    } else {
                        if (line[0].attributes.frompointid !== startEndPoint || pointIds[pointIds.length - 1] !== startEndPoint) {
                            return true;
                        }
                    }

                    return false;
                }
            }
        }
        return result;
    }

    private polylineDeletionCheck() {
        const connectedPointIds: number[] = this.getAllConnectedPointIds(this.getConnectedLines());
        const startEndPoints = this.getEndPoints();
        const selectedLines = this.getSelectedMergeableLines();
        const middlePoints = connectedPointIds.filter(f => f !== startEndPoints[0] && f !== startEndPoints[1]);

        const polylineTypeLookup = this.getLineTypeLookups().find((l: any) => l.featureType === 'polyline');

        let polylinesToDelete = this.spatialUtilService.getLines().filter((l: any) => {
            const pointIds: number[] = this.spatialUtilService.parseIdsAttr(l.attributes.pointids);
            return (
                l.attributes.type === polylineTypeLookup.id &&
                connectedPointIds.some(id => pointIds.includes(id)) &&
                !connectedPointIds.every(id => pointIds.includes(id)) &&
                middlePoints.some(m => pointIds.includes(m))
            );
        });

        polylinesToDelete = polylinesToDelete.filter((l: any) => {
            const pointIds = this.spatialUtilService.parseIdsAttr(l.attributes.pointids);

            if (pointIds.includes(startEndPoints[0]) && pointIds.includes(startEndPoints[1])) {
                return false;
            }

            if (!pointIds.includes(startEndPoints[0])) {
                return this.checkDeletePolyline(selectedLines, pointIds, startEndPoints[0]);
            } else if (!pointIds.includes(startEndPoints[1])) {
                return this.checkDeletePolyline(selectedLines, pointIds, startEndPoints[1]);
            } else {
                return true;
            }
        });

        return polylinesToDelete
            .map((polyline: any) => {
                const polylineId = polyline.attributes.id;
                const subjectListItems = this.spatialUtilService
                    .getInterests()
                    .filter((i: any) => this.spatialUtilService.parseIdsAttr(i.attributes.polylineids).includes(polylineId))
                    .map((i: any) => '\n' + ` ${i.attributes.subject}`)
                    .join('');
                let message = '\n' + ` ${polylineTypeLookup.description} Id ${polylineId}`;
                message = subjectListItems.length > 0 ? message + '\n' + ' Interest Subject Ref(s):' + subjectListItems + '' : message;
                return message;
            })
            .join('');
    }
}
